
import React from 'react';
import { LogOut, Settings, BarChart3, Trash2 } from 'lucide-react';
import SidePanel from '@/components/ui/SidePanel';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import WeeklyProgress from '@/components/profile/WeeklyProgress';
import MonthlyProgress from '@/components/profile/MonthlyProgress';
import UserProfileHeader from './UserProfileHeader';
import UserStatsTab from './UserStatsTab';
import DeleteAccount from '@/components/profile/DeleteAccount';

interface UserPanelProps {
  isOpen: boolean;
  onClose: () => void;
  user: any;
  profile: any;
  memberSince: string;
  todoTasks: number;
  doneTasks: number;
  totalPomodoros: number;
  onLogout: () => void;
  lists: any[];
  tasks: any[];
}

const UserPanel: React.FC<UserPanelProps> = ({
  isOpen,
  onClose,
  user,
  profile,
  memberSince,
  todoTasks,
  doneTasks,
  totalPomodoros,
  onLogout,
  lists,
  tasks
}) => {
  const [currentDailyTarget, setCurrentDailyTarget] = React.useState(profile?.daily_focus_target || 240);
  const [showDeleteAccount, setShowDeleteAccount] = React.useState(false);

  const handleTargetUpdate = (newTarget: number) => {
    setCurrentDailyTarget(newTarget);
  };

  return (
    <SidePanel 
      isOpen={isOpen} 
      onClose={onClose} 
      title="Your profile"
    >
      <div className="px-[25px] py-6">
        {/* Tabbed Content - Moved to top */}
        <Tabs defaultValue="overview" className="mt-0">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="overview" className="text-xs">
              <BarChart3 className="w-4 h-4 mr-1" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="account" className="text-xs">
              <Settings className="w-4 h-4 mr-1" />
              Account
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6 mt-6">
            <UserStatsTab 
              lists={lists}
              tasks={tasks}
              totalPomodoros={totalPomodoros}
            />
            
            <WeeklyProgress 
              tasks={tasks}
              dailyTarget={currentDailyTarget}
              onTargetUpdate={handleTargetUpdate}
            />

            <MonthlyProgress 
              dailyTarget={currentDailyTarget}
            />
          </TabsContent>

          <TabsContent value="account" className="mt-6 space-y-6">
            {/* User Profile Header with inline editing */}
            <UserProfileHeader user={user} profile={profile} memberSince={memberSince} />
            
            {/* Sign Out and Delete Account buttons stacked */}
            <div className="space-y-2">
              <Button 
                className="w-full justify-start text-muted-foreground hover:text-foreground"
                variant="ghost"
                onClick={onLogout}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </Button>

              <Button 
                className="w-full justify-start text-muted-foreground hover:text-foreground"
                variant="ghost"
                onClick={() => setShowDeleteAccount(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Account
              </Button>
            </div>

            {/* Delete Account Modal */}
            {showDeleteAccount && (
              <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                <div className="bg-white dark:bg-gray-900 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-xl font-semibold">Account Settings</h2>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowDeleteAccount(false)}
                      >
                        ×
                      </Button>
                    </div>
                    <DeleteAccount />
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </SidePanel>
  );
};

export default UserPanel;
